import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Option } from '../../../core';
import Button from '../../ui/Button';
import Form from '../../ui/Form';
import RadioGroup from '../../ui/RadioGroup';
import SelectInput from '../../ui/SelectInput';
import { ReferringPractitionerFormInputs } from '../types';
import { renderDefaultReferringPractitionerInputValues } from './helper';
import ReferringPractitionerFormContext from './ReferringPractitionerFormContext';
import ReferringPractitionerInputs from './ReferringPractitionerInputs';

export interface ReferringPractitionerFormProps {
  values?: ReferringPractitionerFormInputs;
  editing: boolean;
  onSelectPractitioner?: (data?: Option) => void;
  onSubmit?: (data: ReferringPractitionerFormInputs) => void;
  onCancel?: () => void;
  loadPractitioners?: any;
  practitioners?: Option[];
  selectedPractitioner?: ReferringPractitionerFormInputs | null;
  hpdUrl?: string;
  isSubmitOnEnter?: boolean;
  showDifferentLocationWarning?: boolean;
  onClickAdd?: (selectedOption?: Option) => void;
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CreateNewWrapper = styled(Button)`
  align-self: flex-end;
  justify-self: end;
  margin-right: 20px;
`;

// this is necessary for gatsby to build using the window object
const isBrowser = typeof window !== 'undefined';

const ReferringPractitionerForm = ({
  values,
  onCancel,
  onSubmit,
  editing,
  loadPractitioners,
  onSelectPractitioner,
  selectedPractitioner,
  hpdUrl,
  isSubmitOnEnter,
  showDifferentLocationWarning,
}: ReferringPractitionerFormProps) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>();
  const [referralType, setReferralType] = useState<string>(
    values?.referralType === 'prenuvo' ? 'Required' : 'Not Required'
  );

  const data = renderDefaultReferringPractitionerInputValues(values);
  const editFormValue = renderDefaultReferringPractitionerInputValues({
    ...selectedPractitioner,
    referralType,
  });

  useEffect(() => {
    if (selectedPractitioner && editing) {
      setShowEditForm(true);
    }
  }, [selectedPractitioner, editing]);

  const handleCancel = () => {
    onSelectPractitioner && onSelectPractitioner();
    setSelectedOption(null);
    setShowEditForm(false);
    onCancel && onCancel();
  };

  const handleSubmit = (data: ReferringPractitionerFormInputs) => {
    onSelectPractitioner && onSelectPractitioner();
    onSubmit && onSubmit({ ...data, prenuvoReferral: referralType });
    if (showEditForm) {
      setShowEditForm(false);
    }
  };

  return (
    <ReferringPractitionerFormContext.Provider
      value={{
        selectedPractitioner,
        values: data,
        editing,
        showDifferentLocationWarning,
      }}
    >
      {editing ? (
        <React.Fragment>
          <TitleContainer>
            <Form.RowLabel>New Referrer</Form.RowLabel>
          </TitleContainer>
          <React.Fragment>
            <Form.Row column={2} margin="0 0 20px 0">
              <SelectInput
                placeholder="Search For a Practitioner"
                label="Change Referrer"
                isAsync
                isClearable
                loadOptions={loadPractitioners}
                onChange={(newSelectedOption: Option) => {
                  setSelectedOption(newSelectedOption);
                  onSelectPractitioner && onSelectPractitioner(newSelectedOption);
                }}
                value={selectedOption}
                showIndicatorAnimation={false}
              />
              <CreateNewWrapper
                size="medium"
                variant="secondary"
                onClick={() => isBrowser && window.open(hpdUrl, '_blank')}
              >
                Create New
              </CreateNewWrapper>
            </Form.Row>
            <Form
              values={editFormValue}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              edit={editing}
              isSubmitOnEnter={isSubmitOnEnter}
            >
              {showEditForm && <ReferringPractitionerInputs />}
              <Form.Controller
                as={''}
                name="prenuvoReferral"
                label="Prenuvo Referral"
                defaultValue={referralType}
                render={(props: any) => (
                  <RadioGroup
                    name={props.name}
                    onChange={(value: string) => {
                      props.onChange(value);
                      setReferralType(value);
                    }}
                    initialValue={referralType}
                    title="Prenuvo Referral"
                    required
                    position="vertical"
                    column={1}
                  >
                    <RadioGroup.Input
                      id="hasReferral"
                      label="Member has a referral from an external practitioner"
                      value="Not Required"
                    />
                    <RadioGroup.Input
                      id="requireReferral"
                      label="Member requires Prenuvo to arrange a referral for them"
                      value="Required"
                    />
                  </RadioGroup>
                )}
              />
            </Form>
          </React.Fragment>
        </React.Fragment>
      ) : (
        <Form
          values={data}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          edit={editing}
          isSubmitOnEnter={isSubmitOnEnter}
        >
          <ReferringPractitionerInputs />
        </Form>
      )}
    </ReferringPractitionerFormContext.Provider>
  );
};

export default ReferringPractitionerForm;
